import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { toast } from 'sonner'
import { Download } from 'lucide-react'
import { Pagination, type PageSize } from '../../components/ui/Pagination'

interface UtmRow {
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  orders: number
  revenue: string
  aov: string
  uniqueCustomers: number
}

interface AttributionResp {
  days: number
  groupBy: 'source' | 'campaign'
  rows: UtmRow[]
}

const PRESETS = [
  { days: 30, label: '30 zile' },
  { days: 90, label: '90 zile' },
  { days: 180, label: '6 luni' },
  { days: 365, label: '12 luni' },
  { days: 730, label: '24 luni' },
]

export function AtributieShopifyPage() {
  const [days, setDays] = useState(90)
  const [groupBy, setGroupBy] = useState<'source' | 'campaign'>('campaign')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(10)

  const query = useQuery<AttributionResp>({
    queryKey: ['shopify-utm', { days, groupBy }],
    queryFn: async () =>
      (await api.get<AttributionResp>(`/shopify/utm-attribution?days=${days}&groupBy=${groupBy}`))
        .data,
  })

  const debugQuery = useQuery<{
    orders: {
      total: number
      cancelled: number
      withUtmSource: number
      withUtmCampaign: number
      withLandingSite: number
      minDate: string | null
      maxDate: string | null
      last30d: number
      last90d: number
      last365d: number
      last730d: number
    }
    topLandingSites: Array<{ landingSite: string | null; count: number }>
  }>({
    queryKey: ['shopify-debug-stats'],
    queryFn: async () => (await api.get('/shopify/debug-stats')).data,
  })

  const filtered = useMemo(() => {
    const rows = query.data?.rows ?? []
    if (!search.trim()) return rows
    const q = search.trim().toLowerCase()
    return rows.filter(
      (r) =>
        (r.utmSource ?? '').toLowerCase().includes(q) ||
        (r.utmMedium ?? '').toLowerCase().includes(q) ||
        (r.utmCampaign ?? '').toLowerCase().includes(q),
    )
  }, [query.data, search])

  const totals = useMemo(() => {
    const rows = filtered
    return {
      orders: rows.reduce((s, r) => s + r.orders, 0),
      revenue: rows.reduce((s, r) => s + Number(r.revenue), 0),
      customers: rows.reduce((s, r) => s + r.uniqueCustomers, 0),
    }
  }, [filtered])

  const totalRevenue = totals.revenue || 1
  const total = filtered.length
  const paginatedRows = filtered.slice(page * pageSize, (page + 1) * pageSize)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Atribuție UTM Shopify</h1>
        <Button
          variant="secondary"
          onClick={async () => {
            try {
              await downloadXlsx(`/shopify/exports/utm?days=${days}`)
              toast.success('Export descărcat')
            } catch {
              toast.error('Eroare la export')
            }
          }}
        >
          <Download className="h-4 w-4" /> Export XLSX
        </Button>
      </div>

      {query.isError && (
        <Card>
          <p className="text-sm text-b4l-red">
            Eroare la încărcare:{' '}
            {(query.error as { response?: { data?: { error?: { message?: string } } } })?.response
              ?.data?.error?.message ?? (query.error as Error).message}
          </p>
        </Card>
      )}

      {debugQuery.data && (
        <Card title="Diagnostic baze de date" description="Distribuția datelor sincronizate">
          <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
            <DebugStat label="Total comenzi" value={debugQuery.data.orders.total} />
            <DebugStat label="Anulate" value={debugQuery.data.orders.cancelled} />
            <DebugStat
              label="Cu landing_site"
              value={debugQuery.data.orders.withLandingSite}
              hint={`${((debugQuery.data.orders.withLandingSite / Math.max(debugQuery.data.orders.total, 1)) * 100).toFixed(0)}%`}
            />
            <DebugStat
              label="Cu utm_source"
              value={debugQuery.data.orders.withUtmSource}
              hint={`${((debugQuery.data.orders.withUtmSource / Math.max(debugQuery.data.orders.total, 1)) * 100).toFixed(0)}%`}
            />
            <DebugStat label="Ultimele 30z" value={debugQuery.data.orders.last30d} />
            <DebugStat label="Ultimele 90z" value={debugQuery.data.orders.last90d} />
            <DebugStat label="Ultim 1 an" value={debugQuery.data.orders.last365d} />
            <DebugStat label="Ultim 2 ani" value={debugQuery.data.orders.last730d} />
          </div>
          <div className="mt-3 text-xs text-b4l-text-muted">
            Perioadă date:{' '}
            {debugQuery.data.orders.minDate
              ? new Date(debugQuery.data.orders.minDate).toLocaleDateString('en-GB')
              : '-'}{' '}
            →{' '}
            {debugQuery.data.orders.maxDate
              ? new Date(debugQuery.data.orders.maxDate).toLocaleDateString('en-GB')
              : '-'}
          </div>
          {debugQuery.data.topLandingSites.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-semibold">Top 10 landing_site (sample):</div>
              <ul className="space-y-0.5 font-mono text-[11px] text-b4l-text-muted">
                {debugQuery.data.topLandingSites.map((l, i) => (
                  <li key={i} className="truncate">
                    [{l.count}×] {l.landingSite ?? '(NULL)'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">Perioadă</label>
            <div className="mt-1 flex flex-wrap gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.days}
                  onClick={() => setDays(p.days)}
                  className={`rounded border px-2 py-1 text-xs ${
                    days === p.days
                      ? 'border-b4l-green bg-b4l-green-light/30 text-b4l-green-dark'
                      : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">Grupare</label>
            <div className="mt-1 flex gap-1">
              <button
                onClick={() => setGroupBy('campaign')}
                className={`rounded border px-2 py-1 text-xs ${
                  groupBy === 'campaign'
                    ? 'border-b4l-green bg-b4l-green-light/30 text-b4l-green-dark'
                    : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
                }`}
              >
                Source × Medium × Campaign
              </button>
              <button
                onClick={() => setGroupBy('source')}
                className={`rounded border px-2 py-1 text-xs ${
                  groupBy === 'source'
                    ? 'border-b4l-green bg-b4l-green-light/30 text-b4l-green-dark'
                    : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
                }`}
              >
                Doar Source × Medium
              </button>
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-b4l-text-dim">Filtru text</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ex: facebook, google, instagram"
              className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        <Kpi label="Comenzi" value={totals.orders.toLocaleString('ro-RO')} />
        <Kpi
          label="Venit (RON)"
          value={totals.revenue.toLocaleString('ro-RO', { maximumFractionDigits: 0 })}
        />
        <Kpi
          label="Clienți unici (sumă pe rând)"
          value={totals.customers.toLocaleString('ro-RO')}
        />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                <th className="px-2 py-2">UTM Source</th>
                <th className="px-2 py-2">UTM Medium</th>
                {groupBy === 'campaign' && <th className="px-2 py-2">UTM Campaign</th>}
                <th className="px-2 py-2 text-right">Comenzi</th>
                <th className="px-2 py-2 text-right">Venit (RON)</th>
                <th className="px-2 py-2 text-right">AOV</th>
                <th className="px-2 py-2 text-right">Clienți</th>
                <th className="px-2 py-2 text-right">% mix</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={8} className="px-2 py-6 text-center text-b4l-text-muted">
                    Se încarcă...
                  </td>
                </tr>
              )}
              {filtered.length === 0 && !query.isLoading && (
                <tr>
                  <td colSpan={8} className="px-2 py-6 text-center text-b4l-text-muted">
                    Niciun rezultat
                  </td>
                </tr>
              )}
              {paginatedRows.map((r, i) => {
                const rev = Number(r.revenue)
                const pct = (rev / totalRevenue) * 100
                return (
                  <tr key={i} className="border-b border-b4l-border hover:bg-b4l-bg-soft">
                    <td className="px-2 py-2 font-mono text-xs">
                      {r.utmSource ?? <span className="text-b4l-text-muted">-</span>}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">
                      {r.utmMedium ?? <span className="text-b4l-text-muted">-</span>}
                    </td>
                    {groupBy === 'campaign' && (
                      <td className="px-2 py-2 font-mono text-xs">
                        {r.utmCampaign ?? <span className="text-b4l-text-muted">-</span>}
                      </td>
                    )}
                    <td className="px-2 py-2 text-right">{r.orders}</td>
                    <td className="px-2 py-2 text-right font-medium">
                      {rev.toLocaleString('ro-RO', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {Number(r.aov).toLocaleString('ro-RO', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-2 text-right">{r.uniqueCustomers}</td>
                    <td className="px-2 py-2 text-right text-xs">{pct.toFixed(1)}%</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(0)
            }}
            itemLabel="grupuri UTM"
          />
        </div>

        <p className="mt-3 text-xs text-b4l-text-muted">
          UTM extras din <code>landing_site</code>. Se numără doar comenzile vândute (fără anulate,
          nelivrate și retururi); venitul e valoarea produselor, fără transport.
          Source/Medium/Campaign NULL = trafic direct sau fără tag.
        </p>
      </Card>
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-b4l-border bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  )
}

function DebugStat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded border border-b4l-border bg-b4l-bg-soft p-2">
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className="font-semibold">{value.toLocaleString('ro-RO')}</div>
      {hint && <div className="text-[10px] text-b4l-text-muted">{hint}</div>}
    </div>
  )
}
